'use client';

import { useEffect, useRef } from 'react';
import type { Locale } from '../HomeLanding';

type Metric = {
  label: string;
  value: string;
};

type StudyCard = {
  id: string;
  tag: string;
  title: string;
  body: string;
  metrics: Metric[];
};

const baseMain: StudyCard = {
  id: 'main',
  tag: 'Purity Assurance',
  title: 'Quality is measurable. Stability is verified.',
  body:
    'A procurement team needed a consistent fish oil supply with low lot-to-lot variation. We tightened multi-stage filtration and dewatering to ensure every shipment met export-grade purity standards.',
  metrics: [
    { label: 'Moisture & impurities', value: '< 1.0%' },
    { label: 'Batch rejection rate', value: '< 0.8%' },
    { label: 'Lot confidence index', value: '99%' },
  ],
};

const baseCards: StudyCard[] = [
  {
    id: 'p1',
    tag: 'Lipid Profiling',
    title: 'Precise EPA and DHA profiles for livestock nutrition.',
    body:
      'Feed formulators require fatty acid stability for aquaculture applications. Batch GC analysis ensures the nutritional profile consistently matches target growth requirements.',
    metrics: [
      { label: 'EPA / DHA deviation', value: 'Â±3%' },
      { label: 'Specification deviation', value: 'Controlled' },
    ],
  },
  {
    id: 'p2',
    tag: 'Oxidation Control',
    title: 'Oxidative stability for a longer shelf life.',
    body:
      'We add antioxidant protection and run periodic stability checks to keep the oil fresh and highly palatable through final use.',
    metrics: [
      { label: 'Peroxide Value', value: '< 3.0 meq/kg' },
      { label: 'Anisidine Value', value: '< 8.0' },
      { label: 'Rancidity risk', value: 'Low' },
    ],
  },
  {
    id: 'p3',
    tag: 'Export Compliance',
    title: 'Complete documentation for smooth global logistics.',
    body:
      'We provide a full document trail, including COA, Health Certificate, and COO, plus clear lot identity for customs clearance and import approval.',
    metrics: [
      { label: 'Compliance gaps', value: '0' },
      { label: 'Document approval', value: '100%' },
      { label: 'Traceability score', value: '100%' },
    ],
  },
  {
    id: 'p4',
    tag: 'Sustainability',
    title: 'A responsible raw material supply chain.',
    body:
      'Every lot can be traced back to legally managed and sustainable fisheries. We follow MarinTrust standards to support a cleaner and more ethical supply chain.',
    metrics: [
      { label: 'Illegal fishing status', value: '0' },
      { label: 'MarinTrust / IFFO compliance', value: '100%' },
      { label: 'Source ethics score', value: 'A+' },
    ],
  },
];

const idMain: StudyCard = {
  id: 'main-id',
  tag: 'Bukti Lapangan',
  title: 'Kualitas Terukur. Stabilitas Terjamin.',
  body:
    'Minimalisir pengotor dengan sistem filtrasi bertahap untuk menjaga variasi antar-lot tetap rendah dan memastikan setiap pengiriman memenuhi standar kemurnian grade ekspor.',
  metrics: [
    { label: 'Kadar air & pengotor', value: '< 1.0%' },
    { label: 'Tingkat penolakan batch', value: '< 0.8%' },
    { label: 'Indeks Kepercayaan Lot', value: '99%' },
  ],
};

const idCards: StudyCard[] = [
  {
    id: 'id-p1',
    tag: 'Profil Lipid',
    title: 'Presisi profil EPA dan DHA untuk nutrisi ternak.',
    body:
      'Formulator pakan memerlukan stabilitas asam lemak untuk aplikasi akuakultur. Analisis GC (Gas Chromatography) per batch kami memastikan profil nutrisi sesuai target pertumbuhan secara konsisten.',
    metrics: [
      { label: 'Konsistensi EPA/DHA', value: 'Â±3%' },
      { label: 'Penyimpangan spesifikasi', value: 'Terkendali' },
    ],
  },
  {
    id: 'id-p2',
    tag: 'Kontrol Oksidasi',
    title: 'Stabilitas oksidatif untuk masa simpan lebih lama.',
    body:
      'Kami menambahkan sistem perlindungan antioksidan dan melakukan uji stabilitas berkala untuk memastikan minyak tetap segar (fresh) dan memiliki palatabilitas tinggi hingga ke tangan pengguna akhir.',
    metrics: [
      { label: 'Peroxide Value', value: '< 3.0 meq/kg' },
      { label: 'Anisidine Value', value: '< 8.0' },
      { label: 'Risiko ketengikan', value: 'Rendah' },
    ],
  },
  {
    id: 'id-p3',
    tag: 'Kepatuhan Ekspor',
    title: 'Dokumentasi lengkap untuk kelancaran logistik global.',
    body:
      'Kami menyediakan jejak dokumen lengkap (COA, Health Certificate, COO) dan identitas lot yang jelas untuk memastikan proses bea cukai dan persetujuan impor berlangsung tanpa kendala.',
    metrics: [
      { label: 'Celah kepatuhan', value: '0' },
      { label: 'Persetujuan dokumen', value: '100%' },
      { label: 'Skor ketertelusuran', value: '100%' },
    ],
  },
  {
    id: 'id-p4',
    tag: 'Keberlanjutan',
    title: 'Jaminan sumber bahan baku yang bertanggung jawab.',
    body:
      'Setiap lot dapat ditelusuri kembali ke sumber perikanan yang dikelola secara legal dan berkelanjutan. Kami mematuhi standar MarinTrust untuk memastikan industri Anda tetap ramah lingkungan dan etis.',
    metrics: [
      { label: 'Status illegal fishing', value: 'Nihil' },
      { label: 'Kepatuhan MarinTrust/IFFO', value: '100%' },
      { label: 'Skor etika sumber', value: 'A+' },
    ],
  },
];

const vnMain: StudyCard = {
  id: 'main-vn',
  tag: 'Chứng thực thực tế',
  title: 'Chất lượng có thể đo lường. Độ ổn định được xác minh.',
  body:
    'Đội ngũ thu mua cần nguồn cung dầu cá ổn định với sai lệch thấp giữa các lô. Chúng tôi siết chặt quy trình lọc nhiều giai đoạn và tách nước để đảm bảo mọi lô hàng đáp ứng tiêu chuẩn độ tinh khiết cấp xuất khẩu.',
  metrics: [
    { label: 'Độ ẩm & tạp chất', value: '< 1.0%' },
    { label: 'Tỷ lệ loại lô', value: '< 0.8%' },
    { label: 'Chỉ số tin cậy lô', value: '99%' },
  ],
};

const vnCards: StudyCard[] = [
  {
    id: 'vn-p1',
    tag: 'Phân tích lipid',
    title: 'Độ chính xác EPA và DHA cho dinh dưỡng vật nuôi.',
    body:
      'Nhà phối trộn thức ăn cần độ ổn định axit béo cho ứng dụng nuôi trồng thủy sản. Phân tích GC theo từng lô giúp hồ sơ dinh dưỡng luôn bám sát mục tiêu tăng trưởng.',
    metrics: [
      { label: 'Sai lệch EPA / DHA', value: '±3%' },
      { label: 'Lệch chuẩn thông số', value: 'Được kiểm soát' },
    ],
  },
  {
    id: 'vn-p2',
    tag: 'Kiểm soát oxy hóa',
    title: 'Ổn định oxy hóa để kéo dài thời hạn sử dụng.',
    body:
      'Chúng tôi bổ sung hệ bảo vệ chống oxy hóa và thực hiện kiểm tra định kỳ để dầu luôn tươi và giữ độ ngon miệng cao đến khâu sử dụng cuối.',
    metrics: [
      { label: 'Peroxide Value', value: '< 3.0 meq/kg' },
      { label: 'Anisidine Value', value: '< 8.0' },
      { label: 'Rủi ro ôi khét', value: 'Thấp' },
    ],
  },
  {
    id: 'vn-p3',
    tag: 'Tuân thủ xuất khẩu',
    title: 'Hồ sơ đầy đủ cho logistics toàn cầu thông suốt.',
    body:
      'Chúng tôi cung cấp bộ chứng từ đầy đủ gồm COA, Health Certificate, COO và mã lô rõ ràng để hỗ trợ thông quan và phê duyệt nhập khẩu.',
    metrics: [
      { label: 'Khoảng trống tuân thủ', value: '0' },
      { label: 'Tỷ lệ duyệt chứng từ', value: '100%' },
      { label: 'Điểm truy xuất', value: '100%' },
    ],
  },
  {
    id: 'vn-p4',
    tag: 'Bền vững',
    title: 'Nguồn nguyên liệu có trách nhiệm và minh bạch.',
    body:
      'Mỗi lô đều có thể truy xuất về nguồn đánh bắt hợp pháp và bền vững. Chúng tôi tuân thủ chuẩn MarinTrust để duy trì chuỗi cung ứng sạch và có đạo đức.',
    metrics: [
      { label: 'Tình trạng khai thác IUU', value: '0' },
      { label: 'Tuân thủ MarinTrust / IFFO', value: '100%' },
      { label: 'Điểm đạo đức nguồn', value: 'A+' },
    ],
  },
];

const cnMain: StudyCard = {
  id: 'main-cn',
  tag: '现场验证',
  title: '质量可衡量，稳定可验证。',
  body:
    '采购团队需要批次波动低、稳定一致的鱼油供应。我们强化多级过滤与脱水流程，确保每批货物都满足出口级纯度标准。',
  metrics: [
    { label: '水分与杂质', value: '< 1.0%' },
    { label: '批次拒收率', value: '< 0.8%' },
    { label: '批次可信指数', value: '99%' },
  ],
};

const cnCards: StudyCard[] = [
  {
    id: 'cn-p1',
    tag: '脂质分析',
    title: '面向动物营养的 EPA / DHA 精准控制。',
    body:
      '配方团队需要稳定的脂肪酸结构用于水产应用。按批次进行 GC 分析，确保营养指标持续匹配目标生长需求。',
    metrics: [
      { label: 'EPA / DHA 偏差', value: '±3%' },
      { label: '规格偏差', value: '受控' },
    ],
  },
  {
    id: 'cn-p2',
    tag: '氧化控制',
    title: '更高氧化稳定性，延长货架期。',
    body:
      '我们采用抗氧化保护体系并执行周期性稳定性检测，确保油脂保持新鲜与高适口性，直达终端使用环节。',
    metrics: [
      { label: 'Peroxide Value', value: '< 3.0 meq/kg' },
      { label: 'Anisidine Value', value: '< 8.0' },
      { label: '酸败风险', value: '低' },
    ],
  },
  {
    id: 'cn-p3',
    tag: '出口合规',
    title: '完整文件体系，保障全球物流顺畅。',
    body:
      '我们提供完整文件链（COA、Health Certificate、COO）和清晰批次身份，支持清关与进口审批流程高效推进。',
    metrics: [
      { label: '合规缺口', value: '0' },
      { label: '文件通过率', value: '100%' },
      { label: '追溯评分', value: '100%' },
    ],
  },
  {
    id: 'cn-p4',
    tag: '可持续',
    title: '负责任原料来源，链路可追溯。',
    body:
      '每个批次都可追溯至合法且可持续管理的渔业来源。我们遵循 MarinTrust 标准，确保供应链环保且合规。',
    metrics: [
      { label: 'IUU 捕捞状态', value: '0' },
      { label: 'MarinTrust / IFFO 合规', value: '100%' },
      { label: '来源伦理评分', value: 'A+' },
    ],
  },
];

function MetricRow({ label, value }: Metric) {
  return (
    <div className="flex items-center justify-between gap-4 border-b border-divider/70 py-2.5 last:border-0">
      <span className="text-[12px] text-titanium font-medium">{label}</span>
      <span className="font-mono text-[12px] font-semibold text-charcoal">{value}</span>
    </div>
  );
}

function StudyPanel({
  study,
  large = false,
}: {
  study: StudyCard;
  large?: boolean;
}) {
  return (
    <article
      className={`rounded-2xl border border-divider bg-surface shadow-card overflow-hidden ${
        large ? 'p-7 md:p-8' : 'p-6'
      }`}
    >
      <div className="flex items-center gap-3 mb-5">
        <span className="inline-block h-px w-8 bg-blue" />
        <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-blue font-medium">
          {study.tag}
        </span>
      </div>

      <h3 className={`${large ? 'text-2xl md:text-4xl' : 'text-xl'} font-bold tracking-[-0.03em] text-charcoal leading-tight`}>
        {study.title}
      </h3>

      <p className="mt-4 text-[14px] md:text-[15px] leading-relaxed text-titanium max-w-3xl">
        {study.body}
      </p>

      <div className={`mt-6 ${large ? 'grid gap-3 md:grid-cols-3' : 'space-y-1.5'}`}>
        {study.metrics.map((metric) =>
          large ? (
            <div key={metric.label} className="rounded-xl border border-divider/70 bg-canvas px-4 py-3">
              <div className="text-[11px] uppercase tracking-[0.14em] text-titanium">{metric.label}</div>
              <div className="mt-1 font-mono text-[18px] font-semibold text-charcoal">{metric.value}</div>
            </div>
          ) : (
            <MetricRow key={metric.label} label={metric.label} value={metric.value} />
          )
        )}
      </div>
    </article>
  );
}

export default function CaseStudies({ locale = 'en' }: { locale?: Locale }) {
  const sectionRef = useRef<HTMLDivElement>(null);
  const isIndonesian = locale === 'id';
  const isVietnamese = locale === 'vn';
  const isChinese = locale === 'cn';

  const main = isVietnamese ? vnMain : isIndonesian ? idMain : isChinese ? cnMain : baseMain;
  const cards = isVietnamese ? vnCards : isIndonesian ? idCards : isChinese ? cnCards : baseCards;

  useEffect(() => {
    if (!sectionRef.current) return;
    const items = sectionRef.current.querySelectorAll('.cs-reveal');
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const i = Array.from(items).indexOf(entry.target as Element);
            setTimeout(() => entry.target.classList.add('active'), i * 100);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.08 }
    );
    items.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section id="case-studies" ref={sectionRef} className="py-24 bg-surface-alt scroll-mt-20" aria-label="Case studies">
      <div className="max-w-[1280px] mx-auto px-6 md:px-10">
        <div className="mb-14">
          <div className="flex items-center gap-3 mb-4 cs-reveal reveal">
            <span className="inline-block w-6 h-px bg-blue" />
            <span className="font-mono text-[11px] tracking-[0.18em] text-blue uppercase font-medium">
              {isVietnamese
              ? 'Chứng thực thực tế'
                : isIndonesian
                  ? 'Bukti Lapangan'
                : isChinese
                  ? '现场验证'
                  : 'Field Proof'}
            </span>
          </div>
          <h2
            className="cs-reveal reveal reveal-delay-1 font-bold tracking-[-0.04em] text-charcoal leading-tight"
            style={{ fontSize: 'clamp(2rem, 4vw, 3.5rem)' }}
          >
            {isVietnamese
              ? 'Chất lượng có thể đo lường.'
              : isIndonesian
                ? 'Kualitas Terukur. Stabilitas Terjamin.'
                : isChinese
                  ? '质量可衡量。'
                  : 'Quality is measurable.'}
            <br />
            <span className="text-titanium font-light">
              {isVietnamese
                ? 'Độ ổn định cũng vậy.'
                : isIndonesian
                  ? 'Minimalisir Pengotor dengan Sistem Filtrasi Bertahap.'
                  : isChinese
                    ? '稳定性亦然。'
                    : 'So is stability.'}
            </span>
          </h2>
        </div>

        <div className="cs-reveal reveal reveal-delay-2">
          <StudyPanel study={main} large />
        </div>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-5">
          {cards.map((card) => (
            <div key={card.id} className="cs-reveal reveal">
              <StudyPanel study={card} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

